'use client'

// CartNavCategoryIsland — the interactive per-category header nav item (#118
// server-chrome, Step 3). This is the old components/Header/NavCategory rewired
// onto NARROW per-concern selector hooks instead of a raw useAppState/useActions
// pair:
//   - useCartCount()      → derived cart item count (badge value)
//   - useHeaderDropdown() → { selectedCategory, select } (dropdown open/close)
//   - useCartPanel()      → { openCart } (slide-out cart panel)
// Everything else (markup, classNames, keyboard handling, the mobile-detect
// resize listener, the region-scoped hrefs) is lifted VERBATIM so the rendered
// DOM is pixel-identical to the old NavCategory.
//
// #418 MOUNTED-GATE (preserved VERBATIM): the cart is restored from localStorage
// during App.render(), so on the FIRST client render a returning visitor's cart
// is already non-empty while the SSR HTML rendered with an empty cart. The badge
// is gated on a post-mount `mounted` flag so the first client render matches the
// SSR HTML (no badge) — otherwise the extra <div> produced a React #418/#423
// hydration mismatch. The real count reveals one tick after hydration commits.
// This gate is intentionally NOT hoisted into useCartCount(): keeping it in the
// component is what guarantees byte-identical SSR-vs-first-render for the badge.

import React, { useState, useEffect, useCallback } from 'react'
import SmartLink from '../SmartLink'
import { getPathFromSlug } from '../../utils/urls'
import { regionalHref } from '../../utils/regions'
import { useUrlRegion } from '../../utils/useCurrentRegion'
import Text from '../Text'
import CounterIndication from '../CounterIndication'
import styles from './NavCategory/styles.module.css'
import ChevronDownIconHeader from '../icons/ChevronDownIconHeader'
import NavDropdown from './NavCategory/NavDropdown'
import CartIcon from '../icons/CartIcon'
import { useCartCount } from './useCartCount'
import { useHeaderDropdown } from './useHeaderDropdown'
import { useCartPanel } from './useCartPanel'

function CartNavCategoryIsland(props) {
  const { count } = useCartCount()
  const { selectedCategory, select } = useHeaderDropdown()
  const { openCart } = useCartPanel()
  const pageRegion = useUrlRegion()

  const { title, items } = props
  const singleItem = items.length === 1
  const showDropdown = !singleItem || (!!items.length && items[0].category && items[0].category.forceShowDropdown)

  // #418 mounted-gate (see file header) — VERBATIM from the old NavCategory.
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  const renderItemsInCartIndication = () => {
    if (!mounted) {
      return false
    }

    return count > 0 &&
      <div className={styles.itemsInCartIndicator}>
        <CounterIndication>{count}</CounterIndication>
      </div>
  }

  const iconWrapperClass = (show, expanded) => {
    const classes = [styles.iconWrapper]
    if (expanded) {
      classes.push(styles.iconWrapperExpanded)
    }
    if (!show) {
      classes.push(styles.iconWrapperHidden)
    }
    return classes.join(' ')
  }

  const getSlug = (item) => {
    return item.route.slug.current
  }

  const onMouseEnter = () => {
    select(title)
  }

  const onMouseLeave = () => {
    select(null)
  }

  const onClick = () => {
    const selectedCategoryNext = selectedCategory === title ? null : title
    select(selectedCategoryNext)
  }

  // Keyboard support for dropdown
  const handleKeyDown = useCallback((event) => {
    const isOpen = selectedCategory === title

    switch (event.key) {
      case 'Enter':
      case ' ':
        if (!singleItem) {
          event.preventDefault()
          onClick()
        }
        break
      case 'Escape':
        if (isOpen) {
          event.preventDefault()
          select(null)
        }
        break
      case 'ArrowDown':
        if (!isOpen && showDropdown) {
          event.preventDefault()
          select(title)
        }
        break
      case 'ArrowUp':
        if (isOpen) {
          event.preventDefault()
          select(null)
        }
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, title, singleItem, showDropdown, select])

  // https://stackoverflow.com/questions/66758341/untangling-state-changes-in-onmouseenter-onmouseleave-and-onclick
  const [isMobileNav, setIsMobileNav] = useState(false)

  const detectMobile = () => {
    window.innerWidth < 900 ? setIsMobileNav(true) : setIsMobileNav(false)
  }

  useEffect(() => {
    // on load
    detectMobile()
    // on resize
    window.addEventListener('resize', detectMobile)
    return () => window.removeEventListener('resize', detectMobile)
  }, [])

  const isCart = getSlug(items[0]) === 'shop/cart'

  return (
    <li
      className={styles.wrapper}
      onMouseEnter={() => !isMobileNav && onMouseEnter()}
      onMouseLeave={() => !isMobileNav && onMouseLeave()}
      onClick={() => isMobileNav && onClick()}
    >
      {singleItem ? (
          isCart ? (
            // The cart is a slide-out panel, not a page — render a real button
            // (no href) so left-click opens the panel and right-click → "open in
            // new window" can't navigate to a non-existent /shop/cart route.
            <button
              className={styles.navItemLink}
              type="button"
              aria-label="Shopping cart"
              onClick={() => openCart()}
            >
              <div className={styles.defaultMenuTextWrapper}>
                <CartIcon aria-hidden="true" />
              </div>
              <div className={styles.hamburgerMenuTextWrapper}>
                <CartIcon large aria-hidden="true" />
              </div>
              <div className={iconWrapperClass(showDropdown, selectedCategory === title)}>
                <ChevronDownIconHeader />
              </div>
              { renderItemsInCartIndication() }
            </button>
          ) : (
            <SmartLink href={regionalHref(getPathFromSlug(getSlug(items[0])), pageRegion)} className={`${styles.navItemLink} ${styles.underline}`}>
              <div className={styles.defaultMenuTextWrapper}>
                <Text type="small">{title}</Text>
              </div>
              <div className={styles.hamburgerMenuTextWrapper}>
                <Text type="heading-5">{title}</Text>
              </div>
              <div className={iconWrapperClass(showDropdown, selectedCategory === title)}>
                <ChevronDownIconHeader />
              </div>
            </SmartLink>
          )
        ) : (
          <button
            className={styles.navItemLink}
            type="button"
            aria-expanded={selectedCategory === title}
            aria-haspopup="true"
            onKeyDown={handleKeyDown}
          >
            <div className={styles.defaultMenuTextWrapper}>
              <Text type="small">{title}</Text>
            </div>
            <div className={styles.hamburgerMenuTextWrapper}>
              <Text type="heading-5">{title}</Text>
            </div>
            <div className={iconWrapperClass(showDropdown, selectedCategory === title)}>
              <ChevronDownIconHeader />
            </div>
          </button>
        )}
        {
          showDropdown &&
          <NavDropdown
            items={items}
            show={selectedCategory === title}
          />
        }
    </li>
  )
}

export default CartNavCategoryIsland
